import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isLocked: boolean;
  isDeleted: boolean;
  lockedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isPending?: false;
  avatar?: string | null;
  branches?: Array<{ id: string; name: string; location?: string; isDefault: boolean }>;
}

interface Invitation {
  id: string;
  code: string;
  email: string | null;
  name: null;
  role: string;
  branches: string[];
  status: string;
  expiresAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isPending: true;
}

interface AdminUsersResponse {
  users: User[];
  invitations: Invitation[];
}

// Query key factory for admin users
const adminUsersKeys = {
  all: ['adminUsers'] as const,
  list: () => [...adminUsersKeys.all, 'list'] as const,
};

/**
 * Hook to fetch all users and invitations for admin panel
 * Caches data for 1 minute, invalidated on mutations
 */
export function useAdminUsers() {
  return useQuery<AdminUsersResponse>({
    queryKey: adminUsersKeys.list(),
    queryFn: async () => {
      const response = await api.get<AdminUsersResponse>('/admin/users');
      return response;
    },
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to update user profile (name and role)
 * Invalidates the admin users list cache when done
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      name,
      role,
    }: {
      userId: string;
      name?: string;
      role?: string;
    }) => {
      const response = await api.put(`/admin/users/${userId}`, {
        name,
        role,
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate the list so it refetches when the sidebar closes
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}

/**
 * Hook to lock/unlock a user
 * Invalidates the admin users list cache when done
 */
export function useLockUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      isLocked,
    }: {
      userId: string;
      isLocked: boolean;
    }) => {
      const response = await api.put(`/admin/users/${userId}/lock`, {
        isLocked,
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}

/**
 * Hook to delete a user
 * Invalidates the admin users list cache when done
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.delete(`/admin/users/${userId}`);
      return response;
    },
    onSuccess: () => {
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}

/**
 * Hook to upload user avatar
 * Invalidates the admin users list cache when done
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      file,
      authToken,
    }: {
      userId: string;
      file: File;
      authToken: string;
    }) => {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || ''}/api/admin/users/${userId}/avatar`,
        {
          method: 'PUT',
          body: formData,
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload avatar');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}

/**
 * Hook to add branch to user
 * Invalidates the admin users list cache when done
 */
export function useAddBranch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      branchId,
      isDefault,
    }: {
      userId: string;
      branchId: string;
      isDefault?: boolean;
    }) => {
      const response = await api.put(`/admin/users/${userId}/branches`, {
        branchId,
        isDefault,
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}

/**
 * Hook to remove branch from user
 * Invalidates the admin users list cache when done
 */
export function useRemoveBranch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      branchId,
    }: {
      userId: string;
      branchId: string;
    }) => {
      const response = await api.delete(`/admin/users/${userId}/branches/${branchId}`);
      return response;
    },
    onSuccess: () => {
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}

/**
 * Hook to set default branch for user
 * Invalidates the admin users list cache when done
 */
export function useSetDefaultBranch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      branchId,
    }: {
      userId: string;
      branchId: string;
    }) => {
      const response = await api.put(`/admin/users/${userId}/default-branch`, {
        branchId,
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}

/**
 * Hook to delete an invitation
 * Invalidates the admin users list cache when done
 */
export function useDeleteInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await api.delete(`/admin/invitations/${invitationId}`);
      return response;
    },
    onSuccess: () => {
      // Invalidate the list
      queryClient.invalidateQueries({
        queryKey: adminUsersKeys.list(),
      });
    },
  });
}
